/**
 * SECURITY NOTE: Client-Side Authentication Context
 * 
 * This context provides authentication state (isAuthenticated, user info) for CLIENT-SIDE
 * UI/UX purposes only. It enables showing/hiding UI elements based on auth status.
 * 
 * IMPORTANT: Never use this context for actual authorization decisions.
 * - Client-side state can be manipulated by attackers
 * - All sensitive operations are protected server-side by RLS policies
 * - Edge functions verify admin roles via the user_roles table
 * - This context is for convenience and UX, not security
 * 
 * Server-side protections:
 * - 423 RLS policies enforce data access at database level
 * - Edge functions use verifyAdminRole() from _shared/admin-verification.ts
 * - Session tokens are validated server-side via validate-session-cookie
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { TokenValidationResult } from '@/hooks/useTokenValidation';
import { authLogger } from '@/utils/authLogger';
interface AuthContextType {
  isAuthenticated: boolean;
  user: {
    email: string | null;
    name: string | null;
    paymentStatus: string | null;
  } | null;
  setAuthenticated: (result: TokenValidationResult) => void;
  clearAuth: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Safe hook that returns null if not in provider
export const useAuthSafe = () => {
  return useContext(AuthContext);
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    // Check localStorage on initial mount
    const userToken = localStorage.getItem('user_auth_token');
    const adminToken = localStorage.getItem('admin_auth_token');
    const initialAuth = !!(userToken || adminToken);
    authLogger.debug('AuthContext initial state', { 
      hasUserToken: !!userToken, 
      hasAdminToken: !!adminToken,
      isAuthenticated: initialAuth 
    });
    return initialAuth;
  });

  const [user, setUser] = useState<AuthContextType['user']>(() => {
    // Initialize from localStorage if available
    const email = localStorage.getItem('user_auth_email') || localStorage.getItem('admin_auth_email');
    authLogger.debug('AuthContext initial user', { email });
    return email ? { email, name: null, paymentStatus: null } : null;
  });

  const setAuthenticated = useCallback((result: TokenValidationResult) => {
    authLogger.contextStateUpdate(result.isValid, result.userEmail);
    
    setIsAuthenticated(result.isValid);
    
    if (result.isValid) {
      setUser({
        email: result.userEmail,
        name: result.userName,
        paymentStatus: result.paymentStatus
      });
    } else {
      setUser(null);
    }
  }, []);

  const clearAuth = useCallback(() => {
    authLogger.tokenCleared('clearAuth called');
    setIsAuthenticated(false);
    setUser(null);
  }, []);

  // Listen for storage changes (e.g., logout in another tab)
  useEffect(() => {
    const handleStorageChange = async (e: StorageEvent) => {
      if (e.key === 'user_auth_token' || e.key === 'admin_auth_token') {
        // Check for fresh login grace period to prevent race condition
        const freshLoginAt = localStorage.getItem('fresh_login_grace_at');
        if (freshLoginAt) {
          const elapsed = Date.now() - parseInt(freshLoginAt, 10);
          if (elapsed < 5000) { // 5 second grace period after login
            authLogger.debug('AuthContext ignoring storage change during fresh login grace period');
            return;
          }
        }

        authLogger.storageEvent(e.key, !!e.newValue);
        if (!e.newValue) {
          // Any auth key disappearing → wipe BOTH cache buckets so a stale
          // sibling bucket can't keep the UI feeling "logged in".
          try {
            const { clearValidationCache } = await import('@/hooks/useTokenValidation');
            clearValidationCache();
          } catch {}
          clearAuth();
        }
      }
    };

    // Handle same-tab sign out via custom event
    const handleSignOutEvent = () => {
      authLogger.debug('AuthContext received auth:signout event');
      clearAuth();
    };

    // Handle same-tab sign in via custom event (from installSession)
    const handleSignInEvent = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      authLogger.debug('AuthContext received auth:signin event', detail);
      // The component that called installSession also pre-populated the cache;
      // we just flip the boolean state here. The next useTokenValidation render
      // will pick up the cached profile.
      setIsAuthenticated(true);
      if (detail.email) {
        setUser(prev => prev ?? { email: detail.email, name: null, paymentStatus: null });
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('auth:signout', handleSignOutEvent);
    window.addEventListener('auth:signin', handleSignInEvent as EventListener);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('auth:signout', handleSignOutEvent);
      window.removeEventListener('auth:signin', handleSignInEvent as EventListener);
    };
  }, [clearAuth]);

  const value = useMemo(
    () => ({ isAuthenticated, user, setAuthenticated, clearAuth }),
    [isAuthenticated, user, setAuthenticated, clearAuth]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
